import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import SendIcon from "@mui/icons-material/SendOutlined";
import { DataGrid } from "@mui/x-data-grid";
import { getUsers } from "../../api/adminService";
import { sendAdminNotification } from "../../api/notificationService";

const CLIENT_TYPE = 1;
const EMPTY_SELECTION = { type: "include", ids: new Set() };

export default function Notifications() {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [sendMode, setSendMode] = useState("all"); // "all" | "selected"

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectionModel, setSelectionModel] = useState(EMPTY_SELECTION);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const selectedIds = useMemo(() => Array.from(selectionModel.ids), [selectionModel]);

  useEffect(() => {
    if (sendMode !== "selected") return;
    setLoading(true);
    getUsers({
      type: CLIENT_TYPE,
      search: search || undefined,
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
    })
      .then((data) => {
        setRows(data.items);
        setRowCount(data.totalCount);
      })
      .catch(() => setError("Could not load clients. Is the API running?"))
      .finally(() => setLoading(false));
  }, [sendMode, search, paginationModel]);

  function handleSearchSubmit(e) {
    e.preventDefault();
    setPaginationModel((p) => ({ ...p, page: 0 }));
    setSearch(searchInput);
  }

  const isValid = title.trim().length > 0 && (sendMode === "all" || selectedIds.length > 0);

  async function handleConfirmSend() {
    setSending(true);
    setError("");
    try {
      const data = await sendAdminNotification({
        title: title.trim(),
        body: body.trim() || undefined,
        imageUrl: imageUrl.trim() || undefined,
        sendToAll: sendMode === "all",
        userIds: sendMode === "selected" ? selectedIds : undefined,
      });
      setResult(data);
      setConfirmOpen(false);
      setTitle("");
      setBody("");
      setImageUrl("");
      setSelectionModel(EMPTY_SELECTION);
    } catch (err) {
      setError(err.response?.data || "Could not send the notification.");
    } finally {
      setSending(false);
    }
  }

  const columns = useMemo(
    () => [
      {
        field: "fullName",
        headerName: "Client",
        flex: 1.2,
        minWidth: 200,
        renderCell: (params) => (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {params.row.fullName}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
              {params.row.email}
            </Typography>
          </Box>
        ),
      },
      { field: "phone", headerName: "Phone", flex: 0.8, minWidth: 140 },
      {
        field: "hasPushToken",
        headerName: "Push",
        width: 120,
        renderCell: (params) =>
          params.row.hasPushToken ? (
            <Chip size="small" color="success" label="Reachable" />
          ) : (
            <Chip size="small" variant="outlined" label="No token" />
          ),
      },
    ],
    []
  );

  return (
    <Box>
      <Stack sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>
          Send Notification
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Push a message to one client, a selection of clients, or all clients.
        </Typography>
      </Stack>

      {result && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setResult(null)}>
          Sent to {result.recipientCount} client{result.recipientCount === 1 ? "" : "s"} (
          {result.pushReachableCount} received a push notification; the rest will see it in-app next time
          they open the app).
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {typeof error === "string" ? error : "Something went wrong."}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, mb: 3 }}>
        <Stack spacing={2.5}>
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            required
            slotProps={{ htmlInput: { maxLength: 150 } }}
          />
          <TextField
            label="Message"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            fullWidth
            multiline
            rows={3}
            slotProps={{ htmlInput: { maxLength: 1000 } }}
          />
          <TextField
            label="Image URL (optional)"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            fullWidth
          />

          <ToggleButtonGroup
            value={sendMode}
            exclusive
            onChange={(e, value) => value && setSendMode(value)}
            size="small"
          >
            <ToggleButton value="all" sx={{ textTransform: "none", px: 2.5 }}>
              All Clients
            </ToggleButton>
            <ToggleButton value="selected" sx={{ textTransform: "none", px: 2.5 }}>
              Select Clients
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Paper>

      {sendMode === "selected" && (
        <>
          <Paper
            variant="outlined"
            component="form"
            onSubmit={handleSearchSubmit}
            sx={{ p: 2, mb: 2, borderRadius: 3, display: "flex", gap: 1.5, alignItems: "center" }}
          >
            <TextField
              size="small"
              placeholder="Search by name, email or phone"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              sx={{ flexGrow: 1, maxWidth: 360 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Button type="submit" variant="outlined" sx={{ borderRadius: 2.5 }}>
              Search
            </Button>
            <Box sx={{ flexGrow: 1, display: { xs: "none", sm: "block" } }} />
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
              {selectedIds.length} selected
            </Typography>
          </Paper>

          <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden", mb: 3 }}>
            <DataGrid
              autoHeight
              rowHeight={60}
              rows={rows}
              getRowId={(row) => row.userId}
              columns={columns}
              rowCount={rowCount}
              loading={loading}
              paginationMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10, 25, 50]}
              checkboxSelection
              keepNonExistentRowsSelected
              rowSelectionModel={selectionModel}
              onRowSelectionModelChange={setSelectionModel}
            />
          </Paper>
        </>
      )}

      <Stack direction="row" justifyContent="flex-end">
        <Button
          variant="contained"
          disableElevation
          startIcon={<SendIcon />}
          disabled={!isValid}
          onClick={() => setConfirmOpen(true)}
          sx={{ borderRadius: 2.5, px: 3, py: 1 }}
        >
          Send Notification
        </Button>
      </Stack>

      <Dialog open={confirmOpen} onClose={() => !sending && setConfirmOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1.5 }}>
          <Typography variant="h6" fontWeight={700}>
            Confirm Send
          </Typography>
          <IconButton onClick={() => setConfirmOpen(false)} size="small" disabled={sending}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2">
            {sendMode === "all"
              ? "This will send the notification to every client. Continue?"
              : `This will send the notification to ${selectedIds.length} selected client${
                  selectedIds.length === 1 ? "" : "s"
                }. Continue?`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmOpen(false)} color="inherit" disabled={sending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            disabled={sending}
            onClick={handleConfirmSend}
            sx={{ borderRadius: 2.5, px: 3 }}
          >
            {sending ? "Sending..." : "Send"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
